import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import type { IxcRegistrationV1Name } from '@interxfi/types/generated/api';
import { AppTextInputWithLens } from '../../../components/form/app-text-input';
import { addDefinedValues } from '../../../util/has-value';

export const nameFormSchema = z.object({
  first: z.string().nonempty({ error: 'First Name is required' }),
  middle: z.string(),
  last: z.string().nonempty({ error: 'Last Name is required' }),
  prefix: z.string(),
  suffix: z.string(),
  preferred: z.string(),
});

export type NameFormValues = z.infer<typeof nameFormSchema>;

export const defaultNameFormValues: NameFormValues = {
  first: '',
  last: '',
  middle: '',
  prefix: '',
  suffix: '',
  preferred: '',
};

export function formValuesToRegistrationName(values: NameFormValues): IxcRegistrationV1Name {
  return addDefinedValues<IxcRegistrationV1Name>(
    {
      first: values.first,
      last: values.last,
    },
    {
      middle: values.middle,
      prefix: values.prefix,
      suffix: values.suffix,
      preferred: values.preferred,
    },
  );
}

export function getInitialFormValuesForRegistrationName(name: IxcRegistrationV1Name | undefined): NameFormValues | undefined {
  if (!name) {
    return undefined;
  }

  return {
    ...defaultNameFormValues,
    ...name,
  };
}

const defaultColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 3,
};

interface NameFormProps {
  columns?: ResponsiveValue<number>;
  lens: Lens<NameFormValues>;
}

export function NameForm({ columns = defaultColumns, lens }: NameFormProps) {
  return (
    <Grid autoColumns={columns} gap={4}>
      <AppTextInputWithLens label="First Name" lens={lens.focus('first')} required />
      <AppTextInputWithLens label="Middle Name" lens={lens.focus('middle')} />
      <AppTextInputWithLens label="Last Name" lens={lens.focus('last')} required />
      <AppTextInputWithLens label="Prefix" lens={lens.focus('prefix')} />
      <AppTextInputWithLens label="Suffix" lens={lens.focus('suffix')} />
      <AppTextInputWithLens label="Preferred Name" lens={lens.focus('preferred')} />
    </Grid>
  );
}
